import { useState } from 'react';
import type { SpreadsheetConfig } from '@mem-ai-hub/shared';
import { generateSpreadsheet } from '@/services/documentApi';

export function tryParseSpreadsheet(text: string): SpreadsheetConfig | null {
  try {
    const obj = JSON.parse(text);
    if (
      obj &&
      typeof obj === 'object' &&
      obj.type === 'spreadsheet' &&
      typeof obj.title === 'string' &&
      typeof obj.fileName === 'string' &&
      Array.isArray(obj.sheets) &&
      obj.sheets.length > 0
    ) {
      return obj as SpreadsheetConfig;
    }
  } catch {
    // not valid spreadsheet JSON
  }
  return null;
}

interface SpreadsheetBlockProps {
  config: SpreadsheetConfig;
}

export default function SpreadsheetBlock({ config }: SpreadsheetBlockProps) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalRows = config.sheets.reduce((sum, s) => sum + s.rows.length, 0);
  const sheetCount = config.sheets.length;

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      const result = await generateSpreadsheet(config);
      window.open(result.downloadUrl, '_blank');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="my-3 rounded-lg border border-border-light bg-bg-secondary p-3">
      <div className="flex items-center gap-3">
        {/* Spreadsheet icon */}
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#03A9F4]/10">
          <svg className="h-5 w-5 text-[#03A9F4]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0112 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M10.875 12c-.621 0-1.125.504-1.125 1.125M12 12c.621 0 1.125.504 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0-1.5v1.5m0 0c0 .621.504 1.125 1.125 1.125m-1.125-1.125c0 .621-.504 1.125-1.125 1.125" />
          </svg>
        </div>

        {/* Title and metadata */}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-text-primary font-heading">
            {config.title}
          </p>
          <p className="text-xs text-text-secondary">
            {sheetCount} {sheetCount === 1 ? 'sheet' : 'sheets'}, {totalRows} {totalRows === 1 ? 'row' : 'rows'}
          </p>
        </div>

        {/* Download button */}
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#03A9F4] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#0288D1] disabled:opacity-50"
        >
          {downloading ? (
            <>
              <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Generating...
            </>
          ) : (
            <>
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Download .xlsx
            </>
          )}
        </button>
      </div>

      {error && (
        <p className="mt-2 text-xs text-red-500">{error}</p>
      )}
    </div>
  );
}
